import { useState } from "react";
import { Minus, Plus, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Field, Surface, Thumb } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";

const STATUS_LABEL = {
  SOLICITADA: "Aguardando a TR3ND",
  APROVADA: "Aprovada",
  EM_TRANSPORTE: "Em transporte",
  RECEBIDA: "Recebida",
  RECUSADA: "Recusada",
  CANCELADA: "Cancelada",
};

const REASONS = [
  { value: "Baixa saída", label: "Baixa saída" },
  { value: "Excesso de estoque", label: "Excesso de estoque" },
  { value: "Produto danificado", label: "Produto danificado" },
  { value: "Outro", label: "Outro" },
];

export function PartnerReturns({ stock, returns, onReload }) {
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState(REASONS[0].value);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const withStock = stock.filter((s) => s.quantity > 0);

  const change = (id, delta, max) =>
    setQty((q) => {
      const next = Math.min(Math.max(0, (q[id] || 0) + delta), max);
      const copy = { ...q, [id]: next };
      if (next === 0) delete copy[id];
      return copy;
    });

  const items = Object.entries(qty).map(([key, quantity]) => {
    const row = withStock.find((s) => (s.key || s.product_id) === key);
    return {
      product_id: row?.product_id || key,
      variation_id: row?.variation_id || null,
      quantity,
    };
  });

  const submit = async () => {
    if (items.length === 0) {
      toast.error("Escolha o que você quer devolver.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/partner/returns", { items, reason, notes });
      toast.success(`Devolução ${data.number} solicitada. Aguarde a TR3ND.`);
      setQty({});
      setNotes("");
      onReload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/partner/returns/${id}/cancel`);
      toast.success("Devolução cancelada.");
      onReload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="partner-returns">
      {withStock.length === 0 ? (
        <EmptyState
          icon={Undo2}
          title="Você não tem produtos para devolver"
          description="Quando houver peças com você, elas aparecem aqui."
        />
      ) : (
        <Surface className="space-y-4 p-5">
          <p className="font-display text-base">O que você quer devolver?</p>
          <ul className="space-y-2">
            {withStock.map((c) => (
              <li
                key={c.key || c.product_id}
                className="flex min-w-0 items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-2.5"
                data-testid={`return-item-${c.key || c.product_id}`}
              >
                <Thumb src={fileUrl(c.photo_path)} alt={c.name} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {c.name}
                    {c.kind === "KIT" && (
                      <span className="ml-1.5 rounded-full border border-primary/40 px-1.5 text-[10px] uppercase text-brand">
                        kit
                      </span>
                    )}
                  </p>
                  <p className="font-num text-[11px] text-muted-foreground">
                    {c.quantity} un comigo
                    {c.stalled ? " · sem venda há bastante tempo" : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-background/60 px-1.5 py-1">
                  <button
                    type="button"
                    onClick={() => change(c.key || c.product_id, -1, c.quantity)}
                    data-testid={`return-dec-${c.key || c.product_id}`}
                    className="p-1 text-muted-foreground"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-7 text-center font-num text-sm" data-testid={`return-qty-${c.key || c.product_id}`}>
                    {qty[c.key || c.product_id] || 0}
                  </span>
                  <button
                    type="button"
                    onClick={() => change(c.key || c.product_id, 1, c.quantity)}
                    data-testid={`return-inc-${c.key || c.product_id}`}
                    className="p-1 text-brand"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <Field label="Motivo">
            <NativeSelect
              value={reason}
              onChange={setReason}
              options={REASONS}
              data-testid="return-reason-select"
            />
          </Field>
          <Field label="Observação">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              data-testid="return-notes-input"
            />
          </Field>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="submit-return-btn"
            className="h-11 w-full rounded-full font-semibold transition-transform active:scale-95"
          >
            {busy ? "Enviando..." : "Solicitar devolução"}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Seu estoque só muda quando a TR3ND confirmar o recebimento.
          </p>
        </Surface>
      )}

      <div>
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Minhas devoluções
        </p>
        {returns.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma devolução ainda.</p>
        ) : (
          <div className="space-y-3">
            {returns.map((r) => (
              <Surface key={r.id} className="min-w-0 overflow-hidden p-4" data-testid={`partner-return-card-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-num text-sm">{r.number || "—"}</span>
                  <span
                    className="shrink-0 rounded-full border border-white/15 bg-secondary/60 px-2.5 py-1 text-xs"
                    data-testid={`partner-return-status-${r.id}`}
                  >
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{dataBR(r.created_at)}</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {r.items.map((it, idx) => (
                    <li key={`${it.product_id}-${it.variation_id || idx}`} className="truncate">
                      <span className="font-num">{it.quantity}x</span> {it.name}
                    </li>
                  ))}
                </ul>
                {r.reason && (
                  <p className="mt-2 text-xs text-muted-foreground">Motivo: {r.reason}</p>
                )}
                {r.status === "SOLICITADA" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cancel(r.id)}
                    data-testid={`cancel-return-btn-${r.id}`}
                    className="mt-3 h-9 rounded-full text-xs"
                  >
                    Cancelar
                  </Button>
                )}
              </Surface>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
